/* eslint-disable no-underscore-dangle,graphql/template-strings */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { adopt } from 'react-adopt';
import gql from 'graphql-tag';
import { Query, Mutation } from 'components/common/Query';
import {
  DELETE_REVIEWS_MUTATION,
  PUBLISH_REVIEWS_MUTATION,
  REPROCESS_REVIEWS_MUTATION,
  REVIEW_DETAILS_FIELDS,
  SEND_REVIEWS_TO_REVIEW_MUTATION,
  UNPUBLISH_REVIEWS_MUTATION,
} from 'components/Review/List';
import ReviewDetails from './Details';

export const ADMIN_REVIEW_QUERY = gql`
  query AdminReview($id: ID!) {
    adminReview(id: $id) {
      ${REVIEW_DETAILS_FIELDS}
    }
  }
`;

export const UPDATE_REVIEW_MUTATION = gql`
  mutation($id: ID!, $rate: Int, $comment: String) {
    updateReview(input: { id: $id, rate: $rate, comment: $comment }) {
      review {
        ${REVIEW_DETAILS_FIELDS}
      }
      errors {
        field
        messages
      }
    }
  }
`;

export class ReviewDetailsContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      actionProcessing: false,
      failedIds: [],
      showNotification: false,
    };
    this.Compose = adopt({
      queryReview: this.review,
      mutatePublish: this.publishReviews,
      mutateUnpublish: this.unpublishReviews,
      mutateReprocess: this.reprocessReviews,
      mutateSendToReview: this.sendReviewsToReview,
      mutateDelete: this.deleteReviews,
      mutateUpdate: this.updateReview,
    });
    this.refetchReview = () => Promise.resolve();
  }

  review = ({ render }) => {
    const { id } = this.props;
    return (
      <Query
        query={ADMIN_REVIEW_QUERY}
        variables={{ id }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, refetch }) => {
          this.refetchReview = refetch;
          return render({ data, loading });
        }}
      </Query>
    );
  };

  publishReviews = ({ render }) => (
    <Mutation mutation={PUBLISH_REVIEWS_MUTATION}>
      {handlePublish => render({ handlePublish })}
    </Mutation>
  );

  unpublishReviews = ({ render }) => (
    <Mutation mutation={UNPUBLISH_REVIEWS_MUTATION}>
      {handleUnpublish => render({ handleUnpublish })}
    </Mutation>
  );

  reprocessReviews = ({ render }) => (
    <Mutation mutation={REPROCESS_REVIEWS_MUTATION}>
      {handleReprocess => render({ handleReprocess })}
    </Mutation>
  );

  sendReviewsToReview = ({ render }) => (
    <Mutation mutation={SEND_REVIEWS_TO_REVIEW_MUTATION}>
      {handleSendToReview => render({ handleSendToReview })}
    </Mutation>
  );

  deleteReviews = ({ render }) => (
    <Mutation mutation={DELETE_REVIEWS_MUTATION}>
      {handleDelete => render({ handleDelete })}
    </Mutation>
  );

  updateReview = ({ render }) => (
    <Mutation mutation={UPDATE_REVIEW_MUTATION}>
      {handleUpdate => render({ handleUpdate })}
    </Mutation>
  );

  runMutation = (items, mutate, responseField) => {
    this.setState({
      actionProcessing: true,
      failedIds: [],
      showNotification: false,
    });
    return mutate({ variables: { ids: items } })
      .then(({ data }) => {
        const successIds = (data[responseField].successIds || []).map(
          id => `${id}`,
        );
        const failedIds = items.filter(item => !successIds.includes(`${item}`));
        this.setState({
          actionProcessing: false,
          failedIds,
          showNotification: failedIds.length > 0,
        });
        return this.refetchReview().catch(() => undefined);
      })
      .catch(() => {
        this.setState({
          actionProcessing: false,
          failedIds: items,
          showNotification: true,
        });
      });
  };

  runUpdate = (input, mutate) => {
    const { id } = this.props;
    this.setState({
      actionProcessing: true,
      failedIds: [],
      showNotification: false,
    });
    return mutate({ variables: { id, ...input } })
      .then(({ data }) => {
        const payload = data.updateReview;
        if (payload.errors && payload.errors.length) {
          this.setState({
            actionProcessing: false,
            failedIds: [id],
            showNotification: true,
          });
          return { errors: payload.errors };
        }
        return this.refetchReview()
          .then(() => {
            this.setState({ actionProcessing: false });
            return { errors: [] };
          })
          .catch(() => {
            this.setState({
              actionProcessing: false,
              failedIds: [id],
              showNotification: true,
            });
            return {
              errors: [{ field: 'review', messages: ['errors.server'] }],
            };
          });
      })
      .catch(() => {
        this.setState({
          actionProcessing: false,
          failedIds: [id],
          showNotification: true,
        });
        return { errors: [{ field: 'review', messages: ['errors.server'] }] };
      });
  };

  handleCloseNotification = () => {
    this.setState({ showNotification: false });
  };

  render() {
    const { id } = this.props;
    const { actionProcessing, failedIds, showNotification } = this.state;

    return (
      <this.Compose>
        {({
          queryReview: { data, loading },
          mutatePublish: { handlePublish },
          mutateUnpublish: { handleUnpublish },
          mutateReprocess: { handleReprocess },
          mutateSendToReview: { handleSendToReview },
          mutateDelete: { handleDelete },
          mutateUpdate: { handleUpdate },
        }) => (
          <ReviewDetails
            data={data && data.adminReview ? data.adminReview : {}}
            loading={loading}
            actionProcessing={actionProcessing}
            publishReview={() =>
              this.runMutation([id], handlePublish, 'publishReviews')
            }
            unpublishReview={() =>
              this.runMutation([id], handleUnpublish, 'unpublishReviews')
            }
            reprocessReview={() =>
              this.runMutation([id], handleReprocess, 'reprocessReviews')
            }
            sendReviewToReview={() =>
              this.runMutation([id], handleSendToReview, 'sendReviewsToReview')
            }
            deleteReview={() =>
              this.runMutation([id], handleDelete, 'deleteReviews')
            }
            updateReview={input => this.runUpdate(input, handleUpdate)}
            failedIds={failedIds}
            showNotification={showNotification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

ReviewDetailsContainer.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ReviewDetailsContainer;
